import Config from "@/core/misc/Config";
import SentryBreadcrumb from "./SentryBreadcrumb";
import ErrorLevel from "./ErrorLevel";

/**
 * Фрейм стека для Sentry API
 */
interface SentryStackFrame {
    filename: string;
    function: string;
    lineno: number;
    colno: number;
    in_app: boolean;
}

/**
 * Событие ошибки для отправки в Sentry
 * Формирует payload согласно Sentry Event API
 */
class SentryEvent {

    id: string;
    timestamp: string;
    platform: string = 'javascript';
    environment: string;
    release: string;
    serverName: string;
    level: ErrorLevel = ErrorLevel.Error;
    tags: Record<string, string> = {};
    contexts: Record<string, any> = {};
    user: { id: string } | null = null;
    attachmentPayload: string | null = null;

    exception: {
        values: Array<{
            type: string;
            value: string;
            stacktrace?: { frames: SentryStackFrame[] };
        }>;
    } | null = null;

    breadcrumbs: SentryBreadcrumb[] = [];

    private originalError: Error | null = null;

    private constructor() {
        this.id = crypto.randomUUID().replace(/-/g, '');
        this.timestamp = new Date().toISOString();
        this.environment = Config.environment || 'unknown';
        this.release = Config.app?.buildId || 'unknown';
        this.serverName = Config.serverName || 'unknown';
    }

    /**
     * Создать событие из Error
     */
    static fromError(
        error: Error,
        tags?: Record<string, string>,
        contexts?: Record<string, any>,
    ): SentryEvent {
        const event = new SentryEvent();
        event.originalError = error;

        event.exception = {
            values: [{
                type: error.name || 'Error',
                value: error.message || 'Unknown error',
                stacktrace: { frames: event.#parseStackTrace(error) }
            }]
        };

        if (tags) {
            Object.entries(tags).forEach(([key, value]) => {
                if (value != null) event.tags[key] = String(value);
            });
        }

        if (contexts) {
            event.contexts = { ...event.contexts, ...contexts };
        }

        event.#setUser();

        return event;
    }

    setBreadcrumbs(breadcrumbs: SentryBreadcrumb[]): this {
        this.breadcrumbs = breadcrumbs;
        return this;
    }

    toPayload(): Record<string, any> {
        const payload: Record<string, any> = {
            event_id: this.id,
            timestamp: this.timestamp,
            platform: this.platform,
            environment: this.environment,
            release: this.release,
            server_name: this.serverName,
            level: this.level,
        };

        if (Object.keys(this.tags).length > 0) payload.tags = this.tags;
        if (Object.keys(this.contexts).length > 0) payload.contexts = this.contexts;
        if (this.user) payload.user = this.user;
        if (this.exception) payload.exception = this.exception;
        if (this.breadcrumbs.length > 0) {
            payload.breadcrumbs = this.breadcrumbs.map(b => b.toPayload());
        }

        return payload;
    }

    getOriginalError(): Error | null {
        return this.originalError;
    }

    #parseStackTrace(error: Error): SentryStackFrame[] {
        if (!error.stack) return [];

        const lines = error.stack.split('\n').slice(1);
        const frames: SentryStackFrame[] = [];

        // Chrome/Edge: "    at functionName (file:line:col)"
        const chromeRegex = /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/;
        // Firefox: "functionName@file:line:col"
        const firefoxRegex = /^(.*)@(.+?):(\d+):(\d+)$/;

        for (const line of lines) {
            let functionName: string;
            let fileName: string;
            let lineNumber: number;
            let colNumber: number;

            const chromeMatch = line.match(chromeRegex);
            const firefoxMatch = line.match(firefoxRegex);

            if (chromeMatch) {
                functionName = chromeMatch[1] || '<anonymous>';
                fileName = chromeMatch[2] || '<anonymous>';
                lineNumber = parseInt(chromeMatch[3], 10) || 0;
                colNumber = parseInt(chromeMatch[4], 10) || 0;
            } else if (firefoxMatch) {
                functionName = firefoxMatch[1] || '<anonymous>';
                fileName = firefoxMatch[2] || '<anonymous>';
                lineNumber = parseInt(firefoxMatch[3], 10) || 0;
                colNumber = parseInt(firefoxMatch[4], 10) || 0;
            } else {
                continue;
            }

            frames.push({
                filename: fileName.split('?')[0],
                function: functionName,
                lineno: lineNumber,
                colno: colNumber,
                in_app: this.#isInAppFrame(fileName)
            });
        }

        return frames.reverse(); // Sentry ожидает обратный порядок
    }

    #isInAppFrame(filename?: string): boolean {
        if (!filename) return false;

        const excludePatterns = [
            'node_modules',
            'webpack-internal',
            'chrome-extension',
            'moz-extension',
            '<anonymous>'
        ];

        return !excludePatterns.some(pattern => filename.includes(pattern));
    }

    #setUser(): void {
        // User устанавливается через setUser() из SentryService
    }

    setUser(userId: string | number | null | undefined): void {
        if (userId) {
            this.user = { id: String(userId) };
        }
    }
}

export default SentryEvent;